import {
  FetchedTenantTypes,
  FetchedWasteTypes,
} from "@/Utils/api/sanity/types";
import { sortByOrderRank } from "@/Utils/wasteStreams";
import { WasteTypeConfig } from "@/pages/configuration/useConfigService";

import { FilterTimeFrameValue } from "server/config-api/index.js";
import { WasteType } from "server/coreApi/accessPoints/types.js";

import { OPTION_ALL } from "./constants";
import { FilterSort, FilterSortMenuType, Option } from "./types";

type TimeOption = {
  option: string;
  value: FilterTimeFrameValue;
  translate?: boolean;
};

// Options must be in ascending order of time length
const AVAILABLE_OPTIONS = [
  {
    option: "periods:today",
    value: FilterTimeFrameValue.Today,
    translate: true,
  },
  {
    option: "periods:7days",
    value: FilterTimeFrameValue.LastWeek,
    translate: true,
  },
  {
    option: "periods:last30days",
    value: FilterTimeFrameValue.Last30Days,
    translate: true,
  },
  {
    option: "periods:last90days",
    value: FilterTimeFrameValue.Last90Days,
    translate: true,
  },
  {
    option: "periods:lastMonth",
    value: FilterTimeFrameValue.LastMonth,
    translate: true,
  },
  {
    option: "periods:yearToDate",
    value: FilterTimeFrameValue.YearToDate,
    translate: true,
  },
  {
    option: "periods:lastYear",
    value: FilterTimeFrameValue.LastYear,
    translate: true,
  },
  {
    option: "periods:last6FullMonths",
    value: FilterTimeFrameValue.Last6FullMonths,
    translate: true,
  },
  {
    option: "periods:lastFullYear",
    value: FilterTimeFrameValue.LastFullYear,
    translate: true,
  },
  {
    option: "periods:sinceBeginning",
    value: FilterTimeFrameValue.SinceBeginning,
    translate: true,
  },
] as TimeOption[];

const getDefaultTimePeriod = (
  defaultValue: FilterTimeFrameValue,
  filteredOptions: TimeOption[],
) => {
  // find next option upwards in time length that is also available in filteredOptions
  const defaultOptionIndex = AVAILABLE_OPTIONS.findIndex(
    ({ value }) => value === defaultValue,
  );

  if (defaultOptionIndex >= 0) {
    const nextOption = AVAILABLE_OPTIONS.findIndex(
      (option, index) =>
        index > defaultOptionIndex &&
        filteredOptions.map((o) => o.value).includes(option.value),
    );

    if (nextOption >= 0) {
      return AVAILABLE_OPTIONS[nextOption]?.value;
    }
  }

  return filteredOptions[0]?.value;
};

export const createTimeFilters = (
  filterProps?: Partial<
    Omit<FilterSort, "options"> & { defaultValue: FilterTimeFrameValue }
  >,
  options?: FilterTimeFrameValue[],
): FilterSort => {
  const filteredOptions = options?.length
    ? AVAILABLE_OPTIONS.filter(({ value }) => options.includes(value))
    : AVAILABLE_OPTIONS;

  let defaultValue = filterProps?.defaultValue;
  const defaultValueNotInFilteredOptions = !filteredOptions.find(
    ({ value }) => value === defaultValue,
  );

  if (defaultValue && defaultValueNotInFilteredOptions) {
    defaultValue = getDefaultTimePeriod(defaultValue, filteredOptions);
  }

  return {
    id: "period",
    type: "filter",
    menuType: FilterSortMenuType.Select,
    title: "filterLabels:period",
    searchEnabled: false,
    ...filterProps,
    defaultValue,
    options: filteredOptions,
  };
};

export const createFractionFilter = (
  fractionList: WasteType[],
  wasteTypes: FetchedWasteTypes | null,
  wasteTypeConfig: WasteTypeConfig[],
): FilterSort => {
  const mappedFractions =
    fractionList?.map((fraction) => {
      const wasteType = wasteTypes?.find(
        ({ id }: { id: any }) => id === fraction.code,
      );
      return {
        option: wasteType
          ? `${
              wasteTypeConfig.find((el) => el.wasteTypeCode === fraction.code)
                ?.name || wasteType.name
            } (${fraction.code})`
          : fraction.code,
        value: fraction.code,
      };
    }) ?? [];

  const sortedFractions = sortByOrderRank(
    mappedFractions,
    wasteTypes || [],
    "value",
  );

  return {
    id: "fractionId",
    type: "filter",
    menuType: FilterSortMenuType.Select,
    options: [
      { option: "filterLabels:allWasteTypes", value: "all", translate: true },
      ...sortedFractions,
    ],
    title: "entities:wasteTypes",
    defaultValue: OPTION_ALL,
  };
};

export const getTenantCategoryOptions = (
  tenantCategories?: FetchedTenantTypes,
): Option[] => [
  { option: "filterLabels:allCustomerTypes", value: "all", translate: true },
  ...(tenantCategories?.map(({ name, id }) => ({ value: id, option: name })) ||
    []),
];
